import React, { useState } from 'react';
import { EyeOffIcon, LockIcon, PencilIcon, PlusIcon, PowerIcon, TimerIcon, Trash2Icon } from 'lucide-react';
import { EncabezadoPagina } from '../components/layout/AppLayout';
import { EncabezadoPanel, EstadoVacio, Panel } from '../components/ui/Basicos';
import { EditorTipoDocumento } from '../components/documentos/EditorTipoDocumento';
import { useDatos } from '../contexts/DatosContext';
import { CATEGORIAS_DOCUMENTO, etiquetaAtributo } from '../data/tiposDocumento';
import type { CategoriaDocumento, TipoDocumento } from '../types';
function tipoVacio(categoria: CategoriaDocumento): TipoDocumento {
  return {
    id: `td-${Date.now()}`,
    nombre: '',
    categoria,
    atributos: [],
    requiereVencimiento: false,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true
  };
}
export function CatalogoDocumentos() {
  const {
    rol,
    tiposDocumento,
    agregarTipoDocumento,
    actualizarTipoDocumento,
    alternarTipoDocumento,
    eliminarTipoDocumento
  } = useDatos();
  const [editando, setEditando] = useState<{
    tipo: TipoDocumento;
    esNuevo: boolean;
  } | null>(null);
  const [aviso, setAviso] = useState('');
  if (rol !== 'Administrador') {
    return <>
        <EncabezadoPagina titulo="Catálogo de documentos" descripcion="Configuración del despacho" data-id="element-1122" data-mp="104zj5xl2e6f0" />
        <Panel data-id="element-1123" data-mp="v3jxc3b2bvvk">
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center" data-id="element-1124" data-mp="1rvdsrhm1owkv">
            <LockIcon className="h-6 w-6 text-gris-300" aria-hidden="true" data-id="element-1125" data-mp="1rtb4b20jjbi9" />
            <p className="text-sm font-semibold text-gris-700" data-id="element-1126" data-mp="1nutu29ltpkwm">Módulo restringido</p>
            <p className="max-w-md text-xs text-gris-500" data-id="element-1127" data-mp="1ite9sjrgzifa">
              Solo el rol Administrador puede definir los tipos de documento y sus requisitos.
            </p>
          </div>
        </Panel>
      </>;
  }
  const guardar = (tipo: TipoDocumento) => {
    if (editando?.esNuevo) {
      agregarTipoDocumento(tipo);
      setAviso(`«${tipo.nombre}» se agregó a ${tipo.categoria}.`);
    } else {
      actualizarTipoDocumento(tipo);
      setAviso(`«${tipo.nombre}» se actualizó.`);
    }
    setEditando(null);
  };
  return <>
      <EncabezadoPagina titulo="Catálogo de documentos" descripcion="Define los tipos de documento del despacho y los atributos que se exigen al subirlos a un expediente." acciones={<button type="button" onClick={() => setEditando({
      tipo: tipoVacio('Documentos procesales'),
      esNuevo: true
    })} className="inline-flex h-9 items-center gap-2 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-1129" data-mp="3hd17cjch920r">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1130" data-mp="3cdy3314bmztu" />
            Agregar tipo de documento
          </button>} data-id="element-1128" data-mp="3r2ktpyzefe0d" />

      <div className="mb-4 grid grid-cols-2 gap-4 xl:grid-cols-4" data-id="element-1131" data-mp="33d05b0kh7ojp">
        {[{
        etiqueta: 'Tipos en catálogo',
        valor: tiposDocumento.length
      }, {
        etiqueta: 'Activos',
        valor: tiposDocumento.filter(t => t.activo).length
      }, {
        etiqueta: 'Con fecha de vencimiento',
        valor: tiposDocumento.filter(t => t.requiereVencimiento).length
      }, {
        etiqueta: 'Confidenciales',
        valor: tiposDocumento.filter(t => t.confidencial).length
      }].map(t => <div key={t.etiqueta} className="rounded-lg border border-gris-200 bg-white px-5 py-4" data-id="element-1132" data-mp="32ohlqzv7wqop">
            <p className="text-xs font-medium text-gris-500" data-id="element-1133" data-mp="2xqg3gh5m8vea">{t.etiqueta}</p>
            <p className="tabular mt-1.5 text-2xl font-semibold text-marino-900" data-id="element-1134" data-mp="3ceu7gverqpwf">{t.valor}</p>
          </div>)}
      </div>

      {editando && <Panel className="mb-4 overflow-hidden" data-id="element-1135" data-mp="3lgjwxvp3berk">
          <EditorTipoDocumento tipo={editando.tipo} esNuevo={editando.esNuevo} onGuardar={guardar} onCancelar={() => setEditando(null)} data-id="element-1136" data-mp="3lhd776n9cmeg" />
        </Panel>}

      {aviso && <p role="status" className="mb-4 rounded border border-exito-100 bg-exito-50 px-4 py-2 text-xs font-medium text-exito-700" data-id="element-1137" data-mp="1eqgwtuanzf7r">
          {aviso}
        </p>}

      <div className="space-y-4" data-id="element-1138" data-mp="1okgytnang4c9">
        {CATEGORIAS_DOCUMENTO.map(categoria => {
        const tipos = tiposDocumento.filter(t => t.categoria === categoria);
        return <Panel key={categoria} data-id="element-1139" data-mp="vvjt0jnmk00e">
              <EncabezadoPanel titulo={categoria} descripcion={`${tipos.length} tipos · ${tipos.filter(t => t.activo).length} activos`} accion={<button type="button" onClick={() => setEditando({
            tipo: tipoVacio(categoria),
            esNuevo: true
          })} className="text-xs font-semibold text-marino-600 hover:text-marino-800" data-id="element-1141" data-mp="19syj7qlpcbtf">
                    Agregar en esta categoría
                  </button>} data-id="element-1140" data-mp="10s0fl9f6e6bd" />
              {tipos.length === 0 ? <EstadoVacio mensaje="Sin tipos de documento en esta categoría" data-id="element-1142" data-mp="5j6larqyh0ae" /> : <div className="overflow-x-auto scroll-fino" data-id="element-1143" data-mp="rwwftd8e0zk">
                  <table className="w-full min-w-[980px] text-sm" data-id="element-1144" data-mp="qdk2dup4a32">
                    <thead data-id="element-1145" data-mp="3ryhuhx1pe963">
                      <tr className="bg-gris-50 text-left text-2xs uppercase tracking-wide text-gris-500" data-id="element-1146" data-mp="3rx6qbw4cizeg">
                        <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-1147" data-mp="3i0tp8d6h1j9o">
                          Tipo
                        </th>
                        <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1148" data-mp="3e09rb7e2qbmh">
                          Atributos obligatorios
                        </th>
                        <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1149" data-mp="1c7vbcdv3bh8p">
                          Reglas
                        </th>
                        <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1150" data-mp="179tve1syny1g">
                          Materias
                        </th>
                        <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1151" data-mp="1qbkkkazp6u7j">
                          Estado
                        </th>
                        <th scope="col" className="px-5 py-2.5 text-right font-semibold" data-id="element-1152" data-mp="1hdidbdpdf5bt">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gris-100" data-id="element-1153" data-mp="2a2futelrcy3i">
                      {tipos.map(t => <tr key={t.id} className={`align-top ${t.activo ? '' : 'bg-gris-50/60'}`} data-id="element-1154" data-mp="2e1oo6icvytyj">
                          <td className="px-5 py-3" data-id="element-1155" data-mp="202f6tfcy1tsz">
                            <p className={`text-sm font-medium ${t.activo ? 'text-marino-900' : 'text-gris-400'}`} data-id="element-1156" data-mp="252bb7fpjzn92">
                              {t.nombre}
                            </p>
                          </td>
                          <td className="max-w-[24rem] px-3 py-3" data-id="element-1157" data-mp="m2apirm4cv5e">
                            {t.atributos.length === 0 ? <span className="text-2xs text-gris-400" data-id="element-1158" data-mp="h493pyxb6hq8">
                                Sin atributos obligatorios
                              </span> : <div className="flex flex-wrap gap-1.5" data-id="element-1159" data-mp="q34cnirqm5x8">
                                {t.atributos.map(a => <span key={a} className="rounded bg-gris-100 px-1.5 py-0.5 text-2xs text-gris-600" data-id="element-1160" data-mp="3ujp2j1mbrzmy">
                                    {etiquetaAtributo(a)}
                                  </span>)}
                              </div>}
                          </td>
                          <td className="px-3 py-3" data-id="element-1161" data-mp="3auwo4yo3mj3m">
                            <div className="flex flex-col gap-1" data-id="element-1162" data-mp="2qa4fhhoxp1z3">
                              {t.requiereVencimiento && <span className="inline-flex items-center gap-1.5 text-2xs font-medium text-ambar-700" data-id="element-1163" data-mp="2ubzg0jz1jbbt">
                                  <TimerIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1164" data-mp="2z8zkwrk5m81c" />
                                  Fecha de vencimiento
                                </span>}
                              {t.confidencial && <span className="inline-flex items-center gap-1.5 text-2xs font-medium text-marino-700" data-id="element-1165" data-mp="3d90m684bzhhc">
                                  <EyeOffIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1166" data-mp="3i1bx060m0noo" />
                                  Confidencial
                                </span>}
                              {!t.requiereVencimiento && !t.confidencial && <span className="text-2xs text-gris-400" data-id="element-1167" data-mp="tgj61riac1k">Sin reglas especiales</span>}
                            </div>
                          </td>
                          <td className="max-w-[14rem] px-3 py-3 text-xs text-gris-600" data-id="element-1168" data-mp="1galb60yzomvu">
                            {t.aplicaTodas ? <span className="font-medium text-gris-700" data-id="element-1169" data-mp="1bemgjvn2i8mz">Todas</span> : t.materias.join(', ')}
                          </td>
                          <td className="px-3 py-3" data-id="element-1170" data-mp="1z1kqsbklgkcg">
                            <span className={`inline-flex items-center rounded px-2 py-0.5 text-2xs font-medium ring-1 ${t.activo ? 'bg-exito-50 text-exito-700 ring-exito-100' : 'bg-gris-100 text-gris-500 ring-gris-200'}`} data-id="element-1171" data-mp="1z2w4p47r78ha">
                              {t.activo ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="px-5 py-3" data-id="element-1172" data-mp="3qveeeu91l6qt">
                            <div className="flex items-center justify-end gap-1" data-id="element-1173" data-mp="3qwpgmpmua6ft">
                              <button type="button" onClick={() => setEditando({
                        tipo: t,
                        esNuevo: false
                      })} aria-label={`Editar ${t.nombre}`} className="flex h-8 w-8 items-center justify-center rounded border border-transparent text-gris-400 transition-colors duration-150 hover:border-gris-200 hover:text-marino-700" data-id="element-1174" data-mp="8quo4hdfj307">
                                <PencilIcon className="h-4 w-4" data-id="element-1175" data-mp="mhpot6drydsp" />
                              </button>
                              <button type="button" onClick={() => {
                        alternarTipoDocumento(t.id);
                        setAviso(`«${t.nombre}» quedó ${t.activo ? 'inactivo' : 'activo'}.`);
                      }} aria-label={`${t.activo ? 'Desactivar' : 'Activar'} ${t.nombre}`} className={`flex h-8 w-8 items-center justify-center rounded border border-transparent transition-colors duration-150 hover:border-gris-200 ${t.activo ? 'text-gris-400 hover:text-ambar-600' : 'text-exito-600 hover:text-exito-700'}`} data-id="element-1176" data-mp="11f08v44hgs01">
                                <PowerIcon className="h-4 w-4" data-id="element-1177" data-mp="s8ytoozg67sg" />
                              </button>
                              <button type="button" onClick={() => {
                        eliminarTipoDocumento(t.id);
                        setAviso(`«${t.nombre}» se eliminó del catálogo.`);
                      }} aria-label={`Eliminar ${t.nombre}`} className="flex h-8 w-8 items-center justify-center rounded border border-transparent text-gris-400 transition-colors duration-150 hover:border-peligro-100 hover:text-peligro-600" data-id="element-1178" data-mp="dg55mf640jc6">
                                <Trash2Icon className="h-4 w-4" data-id="element-1179" data-mp="d7ictz3z6e4e" />
                              </button>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}
            </Panel>;
      })}
      </div>
    </>;
}